import lodash from 'lodash'
import { Logger } from 'wdc-cube'
import { ObjectProperty } from '../tcm_filter_manager'
import { DateFilterScope, DateParcelFilterScope, JunctionMode } from '../tcm_scopes'
import { DateRange } from '../tcm_service'
import { FilterPresenter, type FilterPaneOwner } from './tcm_filter_types'

const LOG = Logger.get('TCM-DateFilter')

let UID_GEN = 0

export class TcmPresenterForDateFilter extends FilterPresenter<DateFilterScope, FilterPaneOwner> {
    // Constructor

    constructor(owner: FilterPaneOwner, scope: DateFilterScope, valueProperty: ObjectProperty<DateRange>) {
        super(owner, scope)
        this.valueProperty = valueProperty
        this.fetch = lodash.debounce(() => {
            this.owner.owner.fetchData('').catch(LOG.caught)
        }, 150)
    }

    // :: Fields

    private valueProperty: ObjectProperty<DateRange>

    private fetch: lodash.DebouncedFuncLeading<() => void>

    // :: Getters and Setters

    get owner() {
        return super.owner as FilterPaneOwner
    }

    get size() {
        return this.scope.conjunction.length + this.scope.disjunction.length
    }

    protected override async bindEvents() {
        this.scope.onAddFilter = this.action(this.handleAddFilter)
        this.scope.onClearAllFilters = this.action(this.handleClearAllFilters)
        LOG.debug('initialized')
    }

    override release(): void {
        this.fetch.cancel()
    }

    override clear() {
        this.scope.conjunction.length = 0
        this.scope.disjunction.length = 0
        this.valueProperty.clear()
    }

    override publish() {
        this.valueProperty.clear()

        const emptyPredicate = (scope: DateParcelFilterScope) =>
            lodash.trim(scope.value) === '' && lodash.trim(scope.toValue) === ''
        this.scope.conjunction.removeByCriteria(emptyPredicate)
        this.scope.disjunction.removeByCriteria(emptyPredicate)

        for (const parcelScope of [...this.scope.conjunction, ...this.scope.disjunction]) {
            const value = parcelScope.value.trim()
            if (emptyPredicate(parcelScope)) {
                continue
            }

            const filter: DateRange = {
                from: value || undefined,
                to: parcelScope.toValue || undefined,
                mode: parcelScope.junctionMode
            }
            this.valueProperty.add(filter)
        }
    }

    add(filter: DateRange) {
        const juntionScope = filter.mode === JunctionMode.OR ? this.addDisjuntion() : this.addConjuntion()
        juntionScope.value = filter.from ? `${filter.from}` : ''
        juntionScope.toValue = filter.to ? `${filter.to}` : ''
    }

    addConjuntion() {
        const andScope = new DateParcelFilterScope()
        andScope.uid = `and-${UID_GEN++}`
        andScope.junctionMode = JunctionMode.AND
        andScope.onValueChange = this.action(this.handleValueChange.bind(this, andScope))
        andScope.onClear = this.action(this.handleClear.bind(this, andScope))
        andScope.onEnterKeyPressed = this.action(this.handleEnterKeyPressed)
        andScope.update = this.update

        this.scope.conjunction.push(andScope)
        return andScope
    }

    addDisjuntion() {
        const orScope = new DateParcelFilterScope()
        orScope.uid = `or-${UID_GEN++}`
        orScope.junctionMode = JunctionMode.OR
        orScope.onValueChange = this.action(this.handleValueChange.bind(this, orScope))
        orScope.onClear = this.action(this.handleClear.bind(this, orScope))
        orScope.onEnterKeyPressed = this.action(this.handleEnterKeyPressed)
        orScope.update = this.update
        this.scope.disjunction.push(orScope)
        return orScope
    }

    private async handleClearAllFilters() {
        this.clear()
    }
    private async handleAddFilter(mode: JunctionMode) {
        this.mostrarItemAdicionado()

        if (mode === JunctionMode.AND) {
            this.addConjuntion()
        } else {
            this.addDisjuntion()
        }
    }

    private async handleClear(parcelScope: DateParcelFilterScope) {
        let found = true

        let idx = this.scope.conjunction.findIndex((item) => item.uid === parcelScope.uid)
        if (idx !== -1) {
            this.scope.conjunction.removeByIndex(idx)
            found = true
        }

        idx = this.scope.disjunction.findIndex((item) => item.uid === parcelScope.uid)
        if (idx !== -1) {
            this.scope.disjunction.removeByIndex(idx)
            found = true
        }

        if (found && parcelScope.value) {
            this.fetch.cancel()
            this.fetch()
        }
    }

    private async handleValueChange(parcelScope: DateParcelFilterScope, value: string, source: 'from' | 'to') {
        if (source === 'from') {
            parcelScope.value = value
        } else {
            parcelScope.toValue = value
        }
    }

    private async handleEnterKeyPressed() {
        this.fetch.cancel()
        this.fetch()
    }

    apply(filters: DateRange[] | null | undefined) {
        if (!filters || filters.length === 0) {
            return
        }

        filters.forEach((filter) => {
            if (!filter.from && !filter.to) {
                return
            }

            const itemScope: DateParcelFilterScope =
                filter.mode === JunctionMode.OR ? this.addDisjuntion() : this.addConjuntion()
            if (filter.from) {
                itemScope.value = `${filter.from}`
            }

            if (filter.to) {
                itemScope.toValue = `${filter.to}`
            }
        })
    }
}
